import _ from "lodash";
import React from "react";
import update from "immutability-helper";
import {
  DeviceDesktopOutlined,
  DeviceMobileOutlined,
  EmailOutlined,
  HeadHeartOutlined,
  LoudspeakerOutlined,
  JournalOutlined,
  AttendanceOutlined,
  QuickTaskOutlined,
  GoalsOutlined,
  UnitPlanOutlined,
  ProgressReportOutlined,
  PolicyTickOutlined,
  AssessmentEvaluatedOutlined,
  CommentSquareLeftClassroomOutlined,
  CommentDotsRightOutlined,
  SaaOutlined,
  PpOutlined,
  AssessmentandEvaluationOutlined,
  CpOutlined,
  CasOutlined,
  EeOutlined,
  TheoryofKnowledgeOutlined,
  FamilyOutlined,
  BulbOutlined,
  UserReflectionOutlined,
  AssessmentOutlined,
  CalendarOutlined,
} from "@toddle-edu/ds-icons";
import {
  getConfigurationsFromCacheV2,
  getPreferencesFromCacheV2,
} from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import { parseConfigurationValue } from "Utils";
import {
  getConfigurationViewFromCache,
  getNotificationConfigurationViewFromCache,
  getPermissionsFromCache,
} from "./modules/ConfigurationGraphqlHelpers";
import ACLStore from "lib/aclStore";
import {
  REMOVE_CLASS_LEVEL_MODULE_SETTINGS,
  VIEW_AND_MANAGE_NOTIFICATIONS,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  SNPv2_PERM,
  LIBRARY_PORTAL_TODDLE_LIBRARY,
  ENABLE_ACADEMIC_PROGRAM_CONSUMPTION,
} from "Constants/permissionConstants";
import { getWeekDaysNameArray, WEEK_DAYS } from "Utils/DateTimeHelpers";
import { getSettingValue } from "modules/PermissionModule";
import { colors } from "Constants";
import { source } from "Teacher/routes/NotificationSettings/modules/NotificationSettingsModule";
import ACLStorage from "lib/aclService";

import { getOrganizationId } from "modules/CommonSelectors";

import { isNewDeadlineEnabled } from "Projects/modules/Utils";
import { getIsAccountConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

const SUBMODULE_TYPES = {
  SETTING_BLOCK_MULTI_APP: "SETTING_BLOCK_MULTI_APP",
  SETTING_BLOCK_SINGLE_APP: "SETTING_BLOCK_SINGLE_APP",
  ATTENDANCE_CATEGORY: "ATTENDANCE_CATEGORY",
  ATTENDANCE_MODE: "ATTENDANCE_MODE",
  TURNITIN_API_SETTING: "TURNITIN_API_SETTING",
  USER_NAME_SETTING: "USER_NAME_SETTING",
  USER_NAME_SETTING_V2: "USER_NAME_SETTING_V2",
  PRINT_NAME_SETTINGS: "PRINT_NAME_SETTINGS",
  ASSESSMENT_SETUP: "ASSESSMENT_SETUP",
  EMAIL_CUSTOM_SETTINGS: "EMAIL_CUSTOM_SETTINGS",
  PROJECT_SETTINGS_TABLE: "PROJECT_SETTINGS_TABLE",
};

const _USER_SETTINGS_COVERED_IN_RNP = [
  "EnableEditAttendanceInProgressReportsByTeacher",
  "EnableAddResponseOfAssignmentByTeacher",
  "OverrideFinalGradeSetting",
  "TeacherPortal:updateGradeBoundariesInClass",
  "TeacherPortal:updateAssessmentCategoriesInClass",
  "TeacherPortal:updateStudentsInClass",
  "TeacherPortal:updateTeachersInClass",
  "EnableAnnouncementCreation",
  "EnableEditAttendanceInPastByTeacher",
  "OverrideClassSettings",
];

//Additional permissions need to fetch in module settings configurations query apart from view
const ADDITIONAL_PERMISSIONS_TO_FETCH = [
  "Analytics:ToddleAnalytics",
  LIBRARY_PORTAL_TODDLE_LIBRARY,
  SNPv2_PERM,
];

const validPlatformProps = {
  variant: "subtle",
  size: "xxx-small",
};

const iconStyle = {
  color: colors.textSubtle,
  width: 16,
  height: 16,
};

export const NOTIFICATION_COLUMN_ID_OBJECT = {
  inApp: "inApp",
  push: "push",
  email: "email",
};

const getColumnsForMultiColumnConfigurations = ({
  enableClassConfigSetting,
  platform,
  subModule,
  t,
  sourceFrom,
  moduleName,
  isRnPEnabled,
  curriculumProgramId,
  isAccountSettingsTab,
}) => {
  const isTeacherModuleSettings = _.isEqual(
    sourceFrom,
    "TEACHER_MODULE_SETTINGS",
    false
  );
  const removeClassModuleSettings = ACLStore.can(
    REMOVE_CLASS_LEVEL_MODULE_SETTINGS
  );

  const isRnPNewLabel =
    isRnPEnabled &&
    _.includes(["MODULE_SETTINGS", "CLASS_MODULE_SETTINGS"], moduleName);

  const rnpLabel = _.isEqual(moduleName, "MODULE_SETTINGS")
    ? getIsAccountConfigurationEnabled()
      ? t("configuration:enable_for_school")
      : t("configuration:enable_for_programme")
    : t("configuration:enable_for_class");

  if (
    (_.isEqual(platform, "TEACHER_WEB") && isTeacherModuleSettings) ||
    (_.isEqual(platform, "ADMIN") &&
      !checkIsAdminNotificationManagerEnabled({
        moduleName,
        curriculumProgramId,
      }))
  ) {
    const { staffLabel, studentLabel, parentLabel } = _.get(
      subModule,
      "customLabels",
      {}
    );
    const multiColumnConfig = [
      {
        label:
          staffLabel || isRnPNewLabel
            ? rnpLabel
            : t("configuration:enable_for_teachers"),
        columnId: "staff",
      },
      {
        label:
          studentLabel ||
          t("configuration:visible_to_user", {
            user: t("common:lowercase", {
              text: t("configuration:students"),
            }),
          }),
        columnId: "student",
      },
      {
        label:
          parentLabel ||
          t("configuration:visible_to_user", {
            user: t("common:lowercase", { text: t("configuration:family") }),
          }),
        columnId: "parent",
      },
    ];
    const enableClassColumn = {
      label: t("configuration:edit_for_classes"),
      columnId: "class",
    };
    if (enableClassConfigSetting && !removeClassModuleSettings) {
      multiColumnConfig.push(enableClassColumn);
    }
    return multiColumnConfig;
  } else {
    return [
      {
        label: t("configuration:in_app"),
        columnId: "inApp",
        icon: <DeviceDesktopOutlined style={iconStyle} />,
        tooltip: t("common:inapp_notification_tooltip"),
      },
      {
        label: t("common:email"),
        columnId: "email",
        icon: <EmailOutlined style={iconStyle} />,
        tooltip: t("common:email_notification_setting_tooltip"),
      },
      {
        label: t("configuration:push"),
        columnId: "push",
        icon: <DeviceMobileOutlined style={iconStyle} />,
        tooltip: t("common:push_notification_tooltip"),
      },
    ];
  }
};

const isConfigurationVisible = ({ configuration, configurationsMap }) => {
  let visible = true;
  const dependentConfigurationIds = _.get(
    configuration,
    "metadata.dependentConfigurationIds",
    []
  );
  if (_.size(dependentConfigurationIds) > 0) {
    visible = _.every(dependentConfigurationIds, configurationId =>
      isConfigurationValueDefinedMemoized(configurationsMap[configurationId])
    );
  }

  return visible;
};

const getSubModuleChildrenNodes = ({
  subModule,
  configurationViewResponse,
  configurationsMap,
  permissionsMap,
  newNotificationSettingEnabled,
}) => {
  const filteredConfigurationViewNodes = _.filter(
    configurationViewResponse,
    configurationView =>
      _.isEqual(configurationView.parentView?.id, subModule.id)
  );

  const parsedNodes = _.compact(
    _.map(filteredConfigurationViewNodes, configurationViewNode => {
      const configurationId = _.get(configurationViewNode, "configuration.id");
      const configurationViewProps = _.pick(configurationViewNode, [
        "displaySequence",
        "metadata",
        "uiType",
        "labelKey",
      ]);

      if (newNotificationSettingEnabled) {
        const showConfigurationView = _.get(
          configurationViewProps,
          "metadata.showConfigurationView",
          ""
        );

        if (showConfigurationView) {
          const showConfigurationViewPerm = ACLStore.can(showConfigurationView);
          if (!showConfigurationViewPerm) return null;
        }
      }

      const matchedConfiguration = _.get(configurationsMap, configurationId);
      if (_.get(configurationViewProps, "labelKey", "") === "routine") {
        const showRoutines = ACLStore.can("EnableClassRoutines");
        if (!showRoutines) return null;
      }
      const controlParamId = _.get(
        configurationViewNode,
        "controlParameter.id",
        null
      );
      const rnp = controlParamId
        ? _.get(permissionsMap, controlParamId, null)
        : null;

      const mergedConfigurationObj = {
        ...matchedConfiguration,
        ...configurationViewProps,
        configurationViewId: configurationViewNode.id,
        ...(rnp ? { rnp } : {}),
      };

      const visible = isConfigurationVisible({
        configuration: mergedConfigurationObj,
        configurationsMap,
      });

      return {
        ...mergedConfigurationObj,
        visible,
      };
    })
  );

  const sortedNodes = _.sortBy(parsedNodes, "displaySequence");

  const [extra, nodes] = _.partition(sortedNodes, view =>
    _.isEqual(view.configurationGroup, "STATE_ROOT")
  );

  let finalNodes = nodes;
  if (subModule.uiType === "SETTING_BLOCK_MULTI_APP") {
    const groupedNodes = _.groupBy(nodes, node => _.get(node, "labelKey"));

    finalNodes = _.reduce(
      groupedNodes,
      (result, nodes, labelKey) => [
        ...result,
        {
          labelKey,
          nodes,
          uiType: "BOOLEAN",
          visible: _.every(nodes, { visible: true }), // for multi column layout
          canConfigureCourseModuleSetting: _.every(nodes, nodes =>
            _.get(nodes, "metadata.canConfigureCourseModuleSetting", false)
          ),
          cascadedParentLabel: _.get(
            nodes,
            "0.metadata.cascadeUpdateParent",
            ""
          ),
          cascadedChildNodes: _.reduce(
            nodes,
            (result, node) => {
              const cascadeNode = _.get(node, "metadata.cascadeUpdate", []);
              return _.uniq([...result, ...cascadeNode]);
            },
            []
          ),
        },
      ],
      []
    );
  }

  return { extra, nodes: finalNodes };
};

function checkAccessPermission({ configurations, node }) {
  const { perm, reverseCheck, permissionsToCheck = "one" } = node;
  const permissionsArray = _.isArray(perm) ? perm : [perm];

  const permissionResults = permissionsArray.map(perm => {
    const curriculumPermission = _.find(configurations, {
      configuration: perm,
    });

    if (!curriculumPermission) {
      return ACLStore.can(perm);
    }

    return _.get(curriculumPermission, "value") === "true";
  });

  let permValue;
  if (permissionsToCheck === "all") {
    permValue = permissionResults.every(Boolean);
  } else {
    permValue = permissionResults.some(Boolean);
  }

  return reverseCheck ? !permValue : permValue;
}

const parseConfigurationView = ({
  moduleName,
  configurationViewQueryVariables,
  configurationQueryVariables,
  permissionsQueryVariables,
  showNodesPerm = {},
  sourceFrom,
  enableRnP,
  curriculumProgramId,
  academicYearId,
  isAccountSettingsTab,
}) => {
  const configurationResponse = getConfigurationsFromCacheV2(
    configurationQueryVariables
  );
  const preferenceResponse = getPreferencesFromCacheV2(
    configurationQueryVariables
  );
  const aclControlParameters = enableRnP
    ? getPermissionsFromCache(permissionsQueryVariables)
    : [];
  const rnpPermissions = _.get(
    aclControlParameters,
    "aclControlParameters.0.controlParameters"
  );
  const permissionsMap = _.keyBy(rnpPermissions, "id");
  const configurations = _.get(configurationResponse, "0.configurations", []);
  const preferenceConfigurations = _.get(
    preferenceResponse,
    "0.configurations",
    []
  );
  const allConfigurations = [...configurations, ...preferenceConfigurations];

  if (_.isEmpty(allConfigurations)) return [];

  const configurationsMap = _.keyBy(allConfigurations, "id");

  const configurationViewResponse =
    sourceFrom === source
      ? getNotificationConfigurationViewFromCache(
          configurationViewQueryVariables
        )
      : getConfigurationViewFromCache(configurationViewQueryVariables);

  const subModulesFiltered = _.filter(
    configurationViewResponse,
    view =>
      _.isEqual(view.parentView?.labelKey, moduleName) &&
      !_.isEqual(view.configuration?.configurationGroup, "STATE_ROOT")
  );
  const newNotificationSettingEnabled = isNotificationSettingsScreen(
    moduleName
  );
  const isAdminNotificationManagerEnabled = checkIsAdminNotificationManagerEnabled(
    {
      moduleName,
      curriculumProgramId,
    }
  );
  const subModules = _.map(subModulesFiltered, subModule => {
    const subGroupSubTitle = _.get(subModule, "metadata.subGroupSubTitle");
    const showGridLayout = _.get(subModule, "metadata.showGridLayout");

    const { extra, nodes } = getSubModuleChildrenNodes({
      subModule,
      configurationViewResponse,
      configurationsMap,
      permissionsMap,
      newNotificationSettingEnabled:
        newNotificationSettingEnabled || isAdminNotificationManagerEnabled,
    });

    return {
      ...subModule,
      nodes,
      extra,
      subGroupSubTitle,
      showGridLayout,
    };
  });

  const subModulesVisible = _.reduce(
    subModules,
    (modifiedSubModules, subModule) => {
      let modifiedSubModule = subModule;
      let areNodesVisible = _.every(subModule.nodes, { visible: true });
      /**
       * THere are some user settings which are covered in RnP. Hence, we need to hide them from user settings
       * when RnP consumption is enabled.
       */
      if (
        (moduleName === "USER_SETTINGS" || moduleName === "ATTENDANCE") &&
        ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
      ) {
        subModule.nodes = _.filter(subModule.nodes, node => {
          const { configuration } = node;
          return !_.includes(_USER_SETTINGS_COVERED_IN_RNP, configuration);
        });

        areNodesVisible = !_.isEmpty(subModule.nodes) && areNodesVisible;
      }

      // Adding as a temporary filtering, will be removed completely once permission is enabled for all orgs
      if (
        _.includes(
          [
            "myp_projects_settings",
            "core_settings",
            "internal_and_external_settings",
          ],
          subModule?.labelKey
        ) &&
        isNewDeadlineEnabled()
      ) {
        modifiedSubModule = update(subModule, {
          nodes: {
            $apply: nodes =>
              _.filter(nodes, node => {
                const { configuration } = node;
                return !_.includes(
                  [
                    "AutoLockFilesInMYPProjects",
                    "AutoLockFilesInDPCore",
                    "AutoLockFilesInIA",
                  ],
                  configuration
                );
              }),
          },
        });
        areNodesVisible =
          !_.isEmpty(modifiedSubModule.nodes) && areNodesVisible;
      } else if (_.isEqual(subModule?.labelKey, "planning")) {
        const isLearningCourseEnabled = getIsLearningCoursesEnabled({
          curriculumProgramId,
          academicYearId,
        });
        if (isLearningCourseEnabled) {
          modifiedSubModule = update(modifiedSubModule, {
            nodes: {
              $apply: nodes =>
                _.filter(nodes, node => {
                  const { labelKey } = node;
                  return !_.includes(
                    ["unit_planning", "planning_insights"],
                    labelKey
                  );
                }),
            },
          });
        }
        areNodesVisible =
          !_.isEmpty(modifiedSubModule.nodes) && areNodesVisible;
      }
      let showNodeByPerm = true;
      const showModuleNodes = _.get(showNodesPerm, moduleName);
      if (showModuleNodes) {
        const labelKey = _.get(subModule, "labelKey");
        const subModuleNodesPerm = _.get(showModuleNodes, labelKey, {});

        const {
          perm,
          reverseCheck,
          excludedLabelKeys,
          updatedLabelKeys,
          permissionsToCheck,
          readFromUserAndOrgLevel,
          excludedColumns,
        } = subModuleNodesPerm;

        if (perm) {
          const permValue = reverseCheck
            ? !ACLStore.can(perm, permissionsToCheck, readFromUserAndOrgLevel)
            : ACLStore.can(perm, permissionsToCheck, readFromUserAndOrgLevel);

          if (_.isEmpty(excludedLabelKeys)) {
            showNodeByPerm = permValue;
          } else {
            modifiedSubModule = update(modifiedSubModule, {
              nodes: {
                $apply: nodes =>
                  _.filter(nodes, node => {
                    const labelKey = _.get(node, "labelKey");
                    const nodeConfig = _.get(excludedLabelKeys, labelKey);
                    if (_.isEmpty(nodeConfig)) {
                      if (_.isEmpty(labelKey)) return true;
                      // Check perm and reverse check for individual permission inside a submodule
                      const subModuleNode = _.get(subModuleNodesPerm, labelKey);
                      if (_.isEmpty(subModuleNode)) {
                        return true;
                      }

                      const permValue = checkAccessPermission({
                        configurations,
                        node: subModuleNode,
                      });

                      return permValue;
                    }

                    const { reverseCheck, perm: nodePerm } = nodeConfig;

                    if (nodePerm) {
                      /**
                       * reverseCheck is not applicable to function-based perm checks because
                       * such functions encapsulate their own logic and conditions internally.
                       **/

                      if (_.isFunction(nodePerm)) {
                        return nodePerm({
                          curriculumProgramId,
                          academicYearId,
                        });
                      }
                      return reverseCheck
                        ? !ACLStore.can(nodePerm)
                        : ACLStore.can(nodePerm);
                    }

                    return reverseCheck ? !permValue : permValue;
                  }),
              },
            });
          }
        }

        //custom label
        if (!_.isEmpty(updatedLabelKeys)) {
          modifiedSubModule = update(modifiedSubModule, {
            nodes: {
              $apply: nodes =>
                _.map(nodes, node => {
                  const labelKey = _.get(node, "labelKey");
                  const labelConfig = _.get(updatedLabelKeys, labelKey);
                  if (_.isEmpty(labelConfig)) {
                    return node;
                  }

                  const labelPermFn = _.get(labelConfig, "labelPermFn", null);
                  const labelPerm = _.get(labelConfig, "labelPerm", "");
                  const permValue = labelPermFn
                    ? labelPermFn({
                        curriculumProgramId,
                        academicYearId,
                      })
                    : ACLStore.can(labelPerm);
                  if (permValue) {
                    showNodeByPerm = true;
                    const updatedLabelKey = _.get(
                      labelConfig,
                      "updatedLabel",
                      labelKey
                    );
                    return update(node, {
                      labelKey: { $set: updatedLabelKey },
                    });
                  }
                  return node;
                }),
            },
          });

          //Update label of submodule
          const labelPerm = _.get(updatedLabelKeys, "labelPerm", "");
          if (labelPerm && ACLStore.can(labelPerm)) {
            const oldLabel = _.get(modifiedSubModule, "labelKey", "");
            const updatedLabel = _.get(
              updatedLabelKeys,
              "updatedLabel",
              oldLabel
            );
            showNodeByPerm = true;
            modifiedSubModule = update(modifiedSubModule, {
              labelKey: { $set: updatedLabel },
            });
          }
        }

        if (!_.isEmpty(excludedColumns)) {
          modifiedSubModule = update(modifiedSubModule, {
            nodes: {
              $apply: nodes =>
                _.map(nodes, node => {
                  const labelKey = _.get(node, "labelKey");

                  if (_.has(excludedColumns, labelKey)) {
                    const {
                      perm,
                      columns = [],
                      reverseCheck = false,
                      permFn,
                    } = excludedColumns[labelKey];

                    // Hide column if permission is enabled
                    const permValue = permFn ? permFn() : ACLStore.can(perm);
                    const shouldHideColumn = reverseCheck
                      ? permValue
                      : !permValue;

                    if (shouldHideColumn) {
                      return {
                        ...node,
                        nodes: _.filter(
                          node.nodes,
                          node =>
                            !_.includes(
                              columns,
                              _.get(node, "metadata.columnId")
                            )
                        ),
                      };
                    }
                  }

                  return node;
                }),
            },
          });
        }
      }

      if (_.isEqual("MODULE_SETTINGS", moduleName)) {
        // Filter out subgroups with no nodes after column filtering
        modifiedSubModule = update(modifiedSubModule, {
          nodes: {
            $apply: nodes =>
              _.filter(nodes, node => {
                // If this is a subgroup with nested nodes, check if it has any children
                if (_.has(node, "nodes") && _.isArray(node.nodes)) {
                  return !_.isEmpty(node.nodes);
                }
                // Otherwise, keep the node
                return true;
              }),
          },
        });

        areNodesVisible =
          !_.isEmpty(modifiedSubModule.nodes) && areNodesVisible;
      }

      if (areNodesVisible && showNodeByPerm) {
        modifiedSubModules.push(modifiedSubModule);
      }

      return modifiedSubModules;
    },
    []
  );

  const subModulesSorted = _.sortBy(subModulesVisible, "displaySequence");
  return subModulesSorted;
};

const parseRequiredConfiguration = ({
  configurationViewQueryVariables,
  moduleName,
}) => {
  const configurationViews = getConfigurationViewFromCache(
    configurationViewQueryVariables
  );

  const getRelevantSubmodules = (views, parentModuleName) => {
    return _.filter(
      views,
      view =>
        _.isEqual(view.parentView?.labelKey, parentModuleName) &&
        !_.isEqual(view.configuration?.configurationGroup, "STATE_ROOT")
    );
  };

  const getChildConfigurationViews = (views, parentId) => {
    return _.filter(views, configView =>
      _.isEqual(configView.parentView?.id, parentId)
    );
  };

  const relevantSubmodules = getRelevantSubmodules(
    configurationViews,
    moduleName
  );
  const configurationsToFetch = new Set(ADDITIONAL_PERMISSIONS_TO_FETCH);

  _.map(relevantSubmodules, submodule => {
    const childViews = getChildConfigurationViews(
      configurationViews,
      submodule.id
    );

    _.map(childViews, viewNode => {
      const configuration = _.get(viewNode, "configuration.configuration");
      if (!_.isEmpty(configuration)) {
        configurationsToFetch.add(configuration);
      }
    });
  });

  return Array.from(configurationsToFetch);
};

const getCourseCountForCustomCourses = ({
  customCourses,
  columnConfigurationList,
}) => {
  const customCoursesList = new Set();
  _.forEach(columnConfigurationList, ({ id, rnp }) => {
    let configUniqueCourses;
    if (rnp && rnp.id) {
      configUniqueCourses = _.get(
        customCourses,
        ["customCourseAclData", rnp.id],
        []
      );
    } else {
      configUniqueCourses = _.get(customCourses, ["customCourseData", id], []);
    }
    if (!_.isEmpty(configUniqueCourses)) {
      configUniqueCourses.forEach(course => customCoursesList.add(course));
    }
  });
  return _.get(customCoursesList, "size", 0);
};

const getValidPlatforms = ({ validPlatforms, t }) => {
  const getPlatformKey = () => {
    if (_.size(validPlatforms) === 1) {
      const platform = _.first(validPlatforms);
      return platform === "app" ? "mobile" : platform;
    }
    return "web_and_mobile";
  };

  const validPlatformTooltip =
    _.size(validPlatforms) === 1
      ? t(`configuration:module_exists_for_only_platform`, {
          platforms: t(`configuration:${getPlatformKey()}`),
        })
      : t(`configuration:module_exists_for_platform`, {
          platforms: t(`configuration:${getPlatformKey()}`),
        });
  const validPlatformList = _.map(validPlatforms, platform => {
    switch (platform) {
      case "web":
        return <DeviceDesktopOutlined {...validPlatformProps} />;
      case "app":
        return <DeviceMobileOutlined {...validPlatformProps} />;
      default:
        return null;
    }
  });

  return { validPlatformList, validPlatformTooltip };
};

const getValueByPlatform = ({ channelTypeCategorizedValues, columnId }) => {
  const defaultValue = _.get(
    channelTypeCategorizedValues,
    _.get(NOTIFICATION_COLUMN_ID_OBJECT, columnId)
  );
  return defaultValue;
};

const getConfigurationByPlatform = ({
  columnConfigurationList,
  columnId,
  newNotificationSettingEnabled,
}) => {
  const matchedConfiguration = _.find(
    columnConfigurationList,
    configuration => {
      const configColumnId = _.get(configuration, "metadata.columnId");
      return _.isEqual(configColumnId, columnId);
    }
  );

  if (!matchedConfiguration) return null;

  const channelTypeCategorizedValues = _.get(
    matchedConfiguration,
    "channelTypeCategorizedValues"
  );
  const { valueType } = matchedConfiguration;
  const defaultValue = newNotificationSettingEnabled
    ? getValueByPlatform({ channelTypeCategorizedValues, columnId })
    : _.get(matchedConfiguration, "value");
  const definedValue = _.get(matchedConfiguration, [
    "platformCategorizedValues",
    columnId,
  ]);
  const rnp = _.get(matchedConfiguration, "rnp", null);
  const isStaff = _.isEqual(columnId, "staff");

  const finalValue = definedValue ?? defaultValue;

  const platformValue =
    rnp && isStaff
      ? _.get(rnp, "value", false)
      : parseConfigurationValue({
          value: finalValue,
          valueType,
        });

  const validPlatforms = _.get(
    matchedConfiguration,
    "metadata.validPlatforms",
    []
  );

  return {
    ...matchedConfiguration,
    platformValue,
    validPlatforms,
  };
};

const isConfigurationValueDefined = configuration => {
  const { valueType } = configuration || {};
  const parsedValue = parseConfigurationValue(configuration);
  switch (valueType) {
    case "JSON":
    case "ARRAY":
      return !_.isEmpty(parsedValue);
    default:
      return true;
  }
};

const isConfigurationValueDefinedMemoized = _.memoize(
  isConfigurationValueDefined,
  configuration => `${configuration.id}_${configuration.value}`
);

const getSelectedCoursesForConfiguration = ({
  configuration,
  courses,
  curriculumProgramId,
}) => {
  const disabledEntityGroups = _.get(configuration, "disabledEntityGroups", []);
  courses = _.map(courses, course => ({
    id: _.get(course, "id"),
    title: _.get(course, "title"),
  }));

  let result = [...courses];

  _.forEach(disabledEntityGroups, entityGroup => {
    const courseObj = _.find(entityGroup.entities, { entityType: "COURSE" });
    const courseCurriculumId = _.get(
      courseObj,
      "resolvedEntity.curriculumProgram.id"
    );
    const courseId = _.get(courseObj, "resolvedEntity.id");
    const shouldIncludeCourse = _.some(courses, { id: courseId });
    if (
      _.isEqual(curriculumProgramId, courseCurriculumId) &&
      shouldIncludeCourse
    ) {
      // filter the course if it is present inside disableEntityGroups
      result = _.filter(
        result,
        currCourse => _.get(currCourse, "id") !== courseId
      );
    }
  });
  return result;
};

const getSelectedCoursesForConfigurationList = curriculumProgramGradeTree => {
  const result = [];
  curriculumProgramGradeTree = _.cloneDeep(curriculumProgramGradeTree);
  const curriculumPrograms = _.keys(curriculumProgramGradeTree);
  _.forEach(curriculumPrograms, curriculum => {
    const grades = _.keys(curriculumProgramGradeTree[curriculum]);
    _.forEach(grades, grade => {
      result.push(
        ..._.get(curriculumProgramGradeTree, `${curriculum}.${grade}`, [])
      );
    });
  });
  return result;
};

const getSelectedLearningCoursesForConfigurationList = curriculumProgramGradeTree => {
  const result = [];
  curriculumProgramGradeTree = _.cloneDeep(curriculumProgramGradeTree);
  const curriculumPrograms = _.keys(curriculumProgramGradeTree);
  _.forEach(curriculumPrograms, curriculum => {
    const grades = _.keys(curriculumProgramGradeTree[curriculum]);
    _.forEach(grades, grade => {
      const learningCourses = _.keys(
        curriculumProgramGradeTree[curriculum][grade] || {}
      );

      _.forEach(learningCourses, learningCourse => {
        // Get the course IDs for this learning course and add them to the result
        result.push(
          ..._.get(
            curriculumProgramGradeTree,
            `${curriculum}.${grade}.${learningCourse}`,
            []
          )
        );
      });
    });
  });
  return result;
};
/**
 *
 * @param {*} configuration {
    "id"
    "uidV2"
    "configuration"
    "valueType"
    "value"
    "isLocked"
    "state"
    "channelTypeCategorizedValues": {
        "push",
        "email",
        "inApp"
    },
    "disabledEntityGroups": [],
    "validPlatforms": [],
    "platformCategorizedValues": {
        "__typename": "ConfigurationPlatformCategorizedValue",
        "default"
        "staff": null,
        "parent": null,
        "student": null
    },
    "configurationGroup": "SETTING",
    "parentConfigurations": [],
    "displaySequence": 20,
    "metadata": {
        "plat": "TEACHER_WEB",
        "depth": 2,
        "conf_id": "84430040023962855",
        "columnId": "inApp",
        "showChooseCourses": true,
        "additionalEntities": [
            {
                "entityId": "IN_APP",
                "entityType": "CHANNEL"
            }
        ]
    },
    "uiType": "BOOLEAN",
    "labelKey": "a_comment_is_added_on_a_unit_plan",
    "configurationViewId": "77182591286904547",
    "visible": true
}
 *  @param {*} courses [{
  {
    "__typename": "Course",
    "id": "113952",
    "title": "First CGC Class",
    "grade": {
        "__typename": "Grade",
        "id": "68552",
        "name": "Pre-K",
        "displaySequence": 1,
        "curriculumProgram": {
            "__typename": "UBDCurriculumProgram",
            "id": "14550",
            "type": "UBD"
        }
    }
}
 }]
 * @returns {selectedCurriculumGradeCourseMapping = {
  [curriculumProgramType]:{
    [gradeId]:[coursesId]
  }
 }}
 */
const getSelectedCoursesForConfigurationV2 = ({
  configuration,
  coursesByCurriculumAndGrade,
}) => {
  const configurationObj = _.first(_.get(configuration, "nodes"));
  const disabledEntityGroups = _.get(
    configurationObj,
    "disabledEntityGroups",
    []
  );
  const disabledCourseIds = new Set(
    _.compact(
      _.map(disabledEntityGroups, entityGroup => {
        const courseObj = _.find(entityGroup.entities, {
          entityType: "COURSE",
        });
        return _.get(courseObj, "resolvedEntity.id");
      })
    )
  );
  // Start with all courses selected then filter out disabled ones
  return _.mapValues(coursesByCurriculumAndGrade, gradeMap =>
    _.mapValues(gradeMap, courseIds =>
      _.filter(courseIds, id => !disabledCourseIds.has(id))
    )
  );
};

const getSelectedLearningCoursesForConfigurationV2 = ({
  configuration,
  learningCoursesByCurriculumGrade,
}) => {
  const configurationObj = _.first(_.get(configuration, "nodes"));
  const disabledEntityGroups = _.get(
    configurationObj,
    "disabledEntityGroups",
    []
  );
  const disabledCourseIds = new Set(
    _.compact(
      _.map(disabledEntityGroups, entityGroup => {
        const courseObj = _.find(entityGroup.entities, {
          entityType: "COURSE",
        });
        return _.get(courseObj, "resolvedEntity.id");
      })
    )
  );

  // Start with all courses selected then filter out disabled ones
  return _.mapValues(learningCoursesByCurriculumGrade, gradeMap =>
    _.mapValues(gradeMap, lcMap =>
      _.mapValues(lcMap, courseIds =>
        _.filter(courseIds, id => !disabledCourseIds.has(id))
      )
    )
  );
};

const isConfigurationEnabledForEducator = ({
  columnConfigurationList,
  editedConfigurations,
}) => {
  const educatorConfiguration = getConfigurationByPlatform({
    columnConfigurationList,
    columnId: "staff",
  });

  // Configuration does not exist for staff column, but might be avaiable for other columns
  if (!educatorConfiguration) return true;

  const editedEducatorConfiguration = getEditedConfiguration({
    editedConfigurations,
    configuration: educatorConfiguration,
    columnId: "staff",
  });

  const enabledForEducator = editedEducatorConfiguration
    ? editedEducatorConfiguration.newValue
    : _.get(educatorConfiguration, "platformValue");

  return enabledForEducator;
};

const getEditedConfiguration = ({
  editedConfigurations,
  configuration,
  columnId,
}) =>
  _.find(editedConfigurations, configurationParam => {
    const configurationId = _.get(configurationParam, "configuration.id");

    return (
      _.isEqual(configurationId, configuration?.id) &&
      _.isEqual(configurationParam.columnId, columnId)
    );
  });

// Hourly time options that avoid 00:00:00 as it has issues with cron job
const getTimeDropdownOptions = () => {
  const timeOptions = [];
  for (let hour24 = 1; hour24 < 24; hour24++) {
    const isPM = hour24 > 11;
    const hour12 = hour24 > 12 ? hour24 - 12 : hour24;
    const hour24String = hour24 < 10 ? `0${hour24}` : `${hour24}`;
    timeOptions.push({
      label: `${hour12}:00 ${isPM ? "PM" : "AM"}`,
      value: `${hour24String}:00:00`,
    });
  }
  return timeOptions;
};

const getWeekDaysDropdownOptions = () => {
  const weekDays = getWeekDaysNameArray();
  return _.map(weekDays, (day, idx) => ({
    label: day,
    value: WEEK_DAYS[idx],
  }));
};

const checkIfEmailSettingsEnabled = parsedConfigurations => {
  return _.reduce(
    parsedConfigurations,
    (currentValue, config) => {
      const emailConfigNode = _.find(config?.nodes, node =>
        _.isEqual(node?.configuration, "EnableWeeklySummaryEmailForFamily")
      );
      const emailConfigValue = _.get(emailConfigNode, "value", "false");
      return currentValue || _.isEqual(emailConfigValue, "true");
    },
    false
  );
};

const getConfigurationLabelIcon = ({ labelKey }) => {
  switch (labelKey) {
    case "announcements_notifications":
      return <LoudspeakerOutlined />;
    case "authorization_and_evaluation_notifications":
      return <AssessmentEvaluatedOutlined />;
    case "behaviour_management":
      return <HeadHeartOutlined />;
    case "class_attendance_notifications":
      return <AttendanceOutlined />;
    case "class_portfolio_notifications":
      return <JournalOutlined />;
    case "assignments":
      return <AssessmentOutlined />;
    case "class_stream_notifications":
      return <QuickTaskOutlined />;
    case "family_messaging_notifications":
      return <CommentSquareLeftClassroomOutlined />;
    case "goals_notifications":
      return <GoalsOutlined />;
    case "onboarding_notifications":
      return <CommentDotsRightOutlined />;
    case "planning_notifications":
      return <UnitPlanOutlined />;
    case "progress_reports_notifications":
      return <ProgressReportOutlined />;
    case "school_policy_and_resource_notifications":
      return <PolicyTickOutlined />;
    case "myp_community_project_notifications":
      return <CpOutlined />;
    case "myp_personal_project_notifications":
      return <PpOutlined />;
    case "myp_service_as_action_notifications":
      return <SaaOutlined />;
    case "internal_external_assessment_notifications":
      return <AssessmentandEvaluationOutlined />;
    case "cas_notifications":
      return <CasOutlined />;
    case "extended_essay_notifications":
      return <EeOutlined />;
    case "group_4_project_notifications":
      return <FamilyOutlined />;
    case "tok_essay_notifications":
    case "tok_exhibition_notifications":
      return <TheoryofKnowledgeOutlined />;
    case "reflective_project_notifications":
      return <UserReflectionOutlined />;
    case "service_learning_notifications":
    case "collaborative_sciences_project_notifications":
      return <BulbOutlined />;
    case "calendar_notifications":
      return <CalendarOutlined />;
    default:
      return <LoudspeakerOutlined />;
  }
};

const getSubModuleLabel = ({
  labelKey,
  organizationId,
  t,
  behaviourNomenclatureConstants,
}) => {
  switch (labelKey) {
    case "tagged_in_chat_notifications": {
      const snpModuleLabelSetting = getSettingValue({
        organizationId,
        name: "AppSettingSnpModuleLabel",
      });

      const snpModuleLabel = t(`snp:${snpModuleLabelSetting}`, {
        defaultValue: snpModuleLabelSetting,
      });
      return t(`configuration:${labelKey}`, { moduleName: snpModuleLabel });
    }

    case "behaviour_management": {
      return behaviourNomenclatureConstants?.MODULE;
    }

    default:
      return t(`configuration:${labelKey}`);
  }
};

export const getConfigurationLabel = ({
  configuration,
  t,
  behaviourNomenclatureConstants,
}) => {
  const labelKey = _.get(configuration, "labelKey");

  switch (labelKey) {
    case "an_incident_is_shared_with_you":
    case "an_incident_involving_you_is_updated":
    case "an_incident_involving_your_child_is_updated":
    case "an_incident_is_referred_to_you":
    case "an_incident_created_by_you_or_shared_with_you_is_referred_to_another_staff":
    case "an_incident_created_by_you_or_shared_with_you_is_updated":
    case "an_incident_created_by_you_or_shared_with_you_is_published":
    case "an_incident_involving_your_child_is_created":
    case "an_incident_involving_you_is_created":
    case "an_incident_created_by_you_or_shared_with_you_is_resolved":
    case "an_incident_involving_your_child_is_resolved":
    case "a_incident_is_shared_with_the_staff_admin":
    case "a_incident_is_referred_to_the_staff_admin":
    case "a_incident_created_by_staff_or_shared_with_staff_is_referred_to_another_staff_admin":
    case "a_incident_created_by_staff_or_shared_with_staff_is_updated_admin":
    case "a_incident_created_by_staff_or_shared_with_staff_is_published_admin":
    case "a_incident_created_by_staff_or_shared_with_staff_is_resolved_admin":
    case "a_incident_involving_the_student_is_created_admin":
    case "a_incident_involving_the_student_is_updated_admin":
    case "a_incident_involving_the_student_is_resolved_admin":
    case "teacher_creates_an_incident_involving_their_child":
    case "teacher_updated_an_incident_involving_their_child":
    case "teacher_resolves_an_incident_involving_their_child":
    case "an_incident_involving_you_is_resolved": {
      return t(`configuration:${labelKey}`, {
        incident: behaviourNomenclatureConstants?.INCIDENT,
      });
    }

    case "a_action_is_added_removed_marked_as_complete_in_a_incident_created_by_staff_or_shared_with_them":
    case "an_action_is_added_removed_marked_as_complete_in_an_incident_created_by_you_or_shared_with_you": {
      return t(`configuration:${labelKey}`, {
        action: behaviourNomenclatureConstants?.ACTION,
        incident: behaviourNomenclatureConstants?.INCIDENT,
      });
    }
    case "behaviour": {
      return behaviourNomenclatureConstants?.MODULE;
    }
    case "authorization_and_evaluation": {
      const organizationId = getOrganizationId();
      const snpModuleLabelSetting = getSettingValue({
        organizationId,
        name: "AppSettingSnpModuleLabel",
      });

      const snpModuleLabel = t(`snp:${snpModuleLabelSetting}`, {
        defaultValue: snpModuleLabelSetting,
      });
      return snpModuleLabel;
    }
    default:
      return t(`configuration:${labelKey}`);
  }
};

const getColumnConfigurationList = (
  configuration,
  t,
  behaviourNomenclatureConstants
) => {
  const showConfigurationView = _.get(
    configuration,
    "nodes.0.metadata.showConfigurationView",
    ""
  );
  const showConfigurationViewPerm = showConfigurationView
    ? ACLStore.can(showConfigurationView)
    : true;
  if (!_.get(configuration, "uiType") || !showConfigurationViewPerm)
    return null;

  const configurationLabel = getConfigurationLabel({
    configuration,
    t,
    behaviourNomenclatureConstants,
  });

  const columnConfigurationList = _.get(configuration, "nodes");
  return { configurationLabel, columnConfigurationList };
};

const isNotificationSettingsScreen = moduleName => {
  return moduleName ? _.isEqual(moduleName, "NOTIFICATION_SETTINGS_V2") : true;
};

const checkIsAdminNotificationManagerEnabled = ({
  moduleName,
  curriculumProgramId,
}) => {
  const isValidModule = _.includes(
    [
      "admin_notification_manager_tab_for_staff",
      "admin_notification_manager_tab_for_student",
      "admin_notification_manager_tab_for_parent",
    ],
    moduleName
  );

  if (ACLStore.can(ENABLE_ACADEMIC_PROGRAM_CONSUMPTION)) {
    return isValidModule;
  }

  return (
    ACLStorage.getModuleSettingsValueByContext(VIEW_AND_MANAGE_NOTIFICATIONS, [
      { entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" },
    ]) && isValidModule
  );
};

const getAdminNotificationContext = ({ t, moduleName }) => {
  switch (moduleName) {
    case "admin_notification_manager_tab_for_staff":
      return {
        label: t("common:staff"),
        modalLabel: t("common:staff_members_plural"),
      };
    case "admin_notification_manager_tab_for_student":
      return {
        label: t("common:student"),
        modalLabel: t("common:student_plural"),
      };
    case "admin_notification_manager_tab_for_parent":
      return {
        label: t("common:family"),
        modalLabel: t("common:family_member_plural"),
      };
    default:
      return {
        label: "",
      };
  }
};

const getConfigurationValue = ({ columnConfiguration, configuration }) => {
  const channelTypeCategorizedValues = _.get(
    columnConfiguration,
    "channelTypeCategorizedValues"
  );

  // getting all columnIds which are enable in view
  const enableConfiguration = getNotificationSettingsColumnIdHashMap(
    configuration
  );
  const channelCategories = _.omit(channelTypeCategorizedValues, "__typename");

  //  if columnId view is disable (showing N/A) then don't check columnId value
  return !_.some(_.keys(channelCategories), curr => {
    return (
      _.get(channelCategories, curr) === "false" &&
      _.has(enableConfiguration, curr)
    );
  });
};

const getPlatformConfigurationValue = ({
  configurationNodes,
  columnId,
  t,
  newNotificationSettingEnabled,
}) => {
  let configurationValue = true;
  let totalFalse = 0;
  let notAvailableCount = 0;
  let dashCount = 0; // reperesent number of disable setting for current columnId;
  let selectedCount = 0; // reperesent number of checked setting for current columnId;
  _.forEach(configurationNodes, configurationItem => {
    const configurationObj = getColumnConfigurationList(configurationItem, t);
    const columnConfigurationList = _.get(
      configurationObj,
      "columnConfigurationList"
    );
    const configuration = getConfigurationByPlatform({
      columnConfigurationList,
      columnId,
      newNotificationSettingEnabled,
    });
    const channelTypeCategorizedValues = _.get(
      configuration,
      "channelTypeCategorizedValues"
    );
    const categorizedValueForThisColumn = _.get(
      channelTypeCategorizedValues,
      _.get(NOTIFICATION_COLUMN_ID_OBJECT, columnId)
    );

    const columType = _.chain(columnConfigurationList)
      .filter(node => {
        const displayConfig = _.get(node, "metadata.displayConfig");
        // this will check whether the column setting is enable or not for current module
        const returnValue = displayConfig ? ACLStore.can(displayConfig) : true;
        return returnValue;
      })
      .map(({ metadata }) => metadata.columnId)
      .value();
    // if categorizedValueForThisColumn is false and it's enable in view then set configurationValue to false and increment totalFalse
    if (
      _.isEqual(categorizedValueForThisColumn, "false") &&
      _.includes(columType, columnId)
    ) {
      configurationValue = false;
      totalFalse++;
    } else if (
      _.isEqual(categorizedValueForThisColumn, "NA") &&
      _.includes(columType, columnId)
    ) {
      notAvailableCount++;
    } else if (!_.includes(columType, columnId)) {
      // if columnId is not enable in view then increment dashCount
      dashCount++; // when encounter - increment dashCounter
    } else {
      selectedCount++;
    }
  });
  // if (count of unchecked columnId is equal to total columnId) or (all column item is disable)
  if (
    _.size(configurationNodes) === totalFalse ||
    (totalFalse === 0 && selectedCount === 0)
  )
    return {
      allSelected: false,
      partialSelected: false,
      isAllDash: dashCount === _.size(configurationNodes),
      isAllNA:
        notAvailableCount === _.size(configurationNodes) - dashCount &&
        dashCount !== _.size(configurationNodes),
    };
  // if none of the column item is false and non of the column item is disable (means show -)
  if (totalFalse === 0 && !dashCount)
    return {
      allSelected: true,
      partialSelected: false,
      isAllDash: dashCount === _.size(configurationNodes),
      isAllNA: notAvailableCount === _.size(configurationNodes),
    };
  // if none of the column item is false and some of the column item is disable (means show -)
  else if (totalFalse === 0 && dashCount) {
    return {
      allSelected:
        selectedCount ===
        _.size(configurationNodes) - dashCount - notAvailableCount,
      partialSelected:
        selectedCount !==
        _.size(configurationNodes) - dashCount - notAvailableCount,
      isAllDash: dashCount === _.size(configurationNodes),
      isAllNA:
        notAvailableCount === _.size(configurationNodes) - dashCount &&
        dashCount !== _.size(configurationNodes),
    };
  }

  return {
    allSelected: false,
    partialSelected: selectedCount > 0,
    isAllDash: dashCount === _.size(configurationNodes),
    isAllNA:
      notAvailableCount === _.size(configurationNodes) - dashCount &&
      dashCount !== _.size(configurationNodes),
  };
};

const getAllPlatformConfigurationValue = ({
  filteredParsedConfigurations,
  columnId,
  t,
  adminNotificationManagerEnabled,
}) => {
  let selectedCount = 0;
  let notAvailableCount = 0;
  let dashCount = 0;
  _.forEach(filteredParsedConfigurations, subModule => {
    const {
      allSelected,
      isAllDash,
      isAllNA,
      partialSelected,
    } = getPlatformConfigurationValue({
      configurationNodes: _.get(subModule, "nodes"),
      columnId,
      t,
      newNotificationSettingEnabled: adminNotificationManagerEnabled,
    });
    if (allSelected) {
      selectedCount++;
    } else if (isAllNA) {
      notAvailableCount++;
    } else if (isAllDash) {
      dashCount++;
    }
  });
  return {
    allSelected:
      selectedCount ===
      _.size(filteredParsedConfigurations) - notAvailableCount - dashCount,
    partialSelected:
      selectedCount > 0 &&
      selectedCount !==
        _.size(filteredParsedConfigurations) - notAvailableCount - dashCount,
    isAllNA: notAvailableCount === _.size(filteredParsedConfigurations),
    isAllDash: dashCount === _.size(filteredParsedConfigurations),
  };
};

const updateAdminConfigurations = ({
  filteredParsedConfigurations,
  columnId,
  t,
  newNotificationSettingEnabled,
  platformConfigurationValue,
}) => {
  const modifiedConfigurations = [];
  _.forEach(filteredParsedConfigurations, subModule => {
    _.forEach(_.get(subModule, "nodes"), configurationItem => {
      const configurationObj = getColumnConfigurationList(configurationItem, t);
      if (!configurationObj) {
        return null;
      }
      const columnConfigurationList = _.get(
        configurationObj,
        "columnConfigurationList"
      );
      const configuration = getConfigurationByPlatform({
        columnConfigurationList,
        columnId,
        newNotificationSettingEnabled,
      });
      const channelTypeCategorizedValues = _.get(
        configuration,
        "channelTypeCategorizedValues"
      );
      if (_.get(channelTypeCategorizedValues, columnId) !== "NA") {
        modifiedConfigurations.push({
          newValue: !platformConfigurationValue,
          columnId,
          configuration,
        });
      }
    });
  });
  return modifiedConfigurations;
};

const updateColumnConfigurations = ({
  configurationNodes,
  columnId,
  t,
  newNotificationSettingEnabled,
  platformConfigurationValue,
}) => {
  const modifiedConfigurations = [];
  _.forEach(configurationNodes, configurationItem => {
    const configurationObj = getColumnConfigurationList(configurationItem, t);
    if (!configurationObj) {
      return null;
    }
    const columnConfigurationList = _.get(
      configurationObj,
      "columnConfigurationList"
    );
    const configuration = getConfigurationByPlatform({
      columnConfigurationList,
      columnId,
      newNotificationSettingEnabled,
    });
    const channelTypeCategorizedValues = _.get(
      configuration,
      "channelTypeCategorizedValues"
    );
    if (_.get(channelTypeCategorizedValues, columnId) !== "NA") {
      modifiedConfigurations.push({
        newValue: !platformConfigurationValue,
        columnId,
        configuration,
      });
    }
  });
  return modifiedConfigurations;
};

const getIsParentConfigurationDisabled = ({ cascadedParent, columnId, t }) => {
  if (_.isEmpty(cascadedParent)) return false;

  const columnConfigurationList = _.get(cascadedParent, "nodes", []);
  const configuration = getConfigurationByPlatform({
    columnConfigurationList,
    columnId,
  });

  const platformValue = _.get(configuration, "platformValue", true);
  if (!platformValue) {
    return true;
  }
  return false;
};

const getCascadeModifiedConfigurations = ({
  cascadedNodes,
  columnId,
  isEducatorColumn,
  t,
}) => {
  const modifiedConfigurations = [];
  _.forEach(cascadedNodes, node => {
    const configurationObj = getColumnConfigurationList(node, t);
    if (!configurationObj) return null;

    const columnConfigurationList = _.get(
      configurationObj,
      "columnConfigurationList"
    );
    const configuration = getConfigurationByPlatform({
      columnConfigurationList,
      columnId,
    });
    if (!_.isEmpty(configuration) && configuration.platformValue) {
      modifiedConfigurations.push({
        newValue: false,
        columnId,
        configuration,
      });
    }

    if (isEducatorColumn) {
      const columnsToTurnOff = ["parent", "student"];
      _.forEach(columnsToTurnOff, columnId => {
        const configuration = getConfigurationByPlatform({
          columnConfigurationList,
          columnId,
        });
        if (!_.isEmpty(configuration) && configuration.platformValue) {
          modifiedConfigurations.push({
            newValue: false,
            columnId,
            configuration,
          });
        }
      });
    }
  });
  return modifiedConfigurations;
};

const getUserTypeForModuleSetting = ({ t, columnId }) => {
  switch (columnId) {
    case "staff":
      return _.lowerCase(t("configuration:teachers"));
    case "student":
      return _.lowerCase(t("configuration:students"));
    case "parent":
      return _.lowerCase(t("configuration:family_members"));
  }
};

const getperiodSetFilter = params => params;
const getperiodSetFilterMemoized = _.memoize(
  getperiodSetFilter,
  JSON.stringify
);

const getCustomCoursesQueryVariables = params => params;
const getCustomCoursesQueryVariablesMemoized = _.memoize(
  getCustomCoursesQueryVariables,
  JSON.stringify
);

const getPermissionsQueryVariables = ({
  type,
  currentCurriculumProgramId,
  courses = [],
  controlParameterId,
}) => {
  const contexts = [];

  switch (type) {
    case "CURRICULUM":
      contexts.push([
        {
          entityType: "CURRICULUM_PROGRAM",
          entityId: currentCurriculumProgramId,
        },
      ]);
      break;
    case "COURSES":
      _.map(courses, course => {
        contexts.push([
          {
            entityType: "CURRICULUM_PROGRAM",
            entityId: currentCurriculumProgramId,
          },
          {
            entityType: "COURSE",
            entityId: course,
          },
        ]);
      });
      break;
  }

  return {
    filters: {
      module: "MODULE_SETTINGS",
      appType: "ADMIN_WEB",
      contexts,
      controlParameterTypes: ["MODULE_SETTING", "PERMISSION"],
      excludeRnPRoleValues: true,
      ...(controlParameterId
        ? { controlParameterIds: [controlParameterId] }
        : {}),
    },
  };
};
const getPermissionsQueryVariablesMemoized = _.memoize(
  getPermissionsQueryVariables,
  JSON.stringify
);

const getCurriculumGlobalConstantsQueryFilter = params => params;
const getCurriculumGlobalConstantsQueryFilterMemoized = _.memoize(
  getCurriculumGlobalConstantsQueryFilter,
  JSON.stringify
);

const getConfigInputProps = params => params;
const getConfigInputPropsMemoized = _.memoize(
  getConfigInputProps,
  JSON.stringify
);

const getSubModuleHashMap = filteredParsedConfigurations => {
  return _.reduce(
    filteredParsedConfigurations,
    (prev, module) => {
      const labelKey = _.get(module, "labelKey");
      return { [labelKey]: module, ...prev };
    },
    {}
  );
};

const getNotificationSettingsColumnIdHashMap = configuration => {
  return _.reduce(
    _.get(configuration, "nodes", []),
    (result, currConf) => {
      const columnId = _.get(currConf, "metadata.columnId");
      return { ...result, [columnId]: columnId };
    },
    {}
  );
};

const getAdminMasterConfiguration = (
  masterAdminConfiguration,
  configurationQueryVariables
) => {
  const configurationResponse = getConfigurationsFromCacheV2(
    configurationQueryVariables
  );
  const preferenceResponse = getPreferencesFromCacheV2(
    configurationQueryVariables
  );
  const configurations = _.get(configurationResponse, "0.configurations", []);
  const preferenceConfigurations = _.get(
    preferenceResponse,
    "0.configurations",
    []
  );
  const allConfigurations = [...configurations, ...preferenceConfigurations];
  const configurationsMap = _.keyBy(allConfigurations, "id");
  const configurationId = _.get(masterAdminConfiguration, "configuration.id");
  const matchedConfiguration = _.get(configurationsMap, configurationId);
  return matchedConfiguration;
};

const checkIfWSEDisplayLogsEnabled = () =>
  ACLStore.can("FeatureFlag:WSEDisplayLogsEnabled");

const getNotificationSettingsConfig = ({
  parsedConfigurations,
  columnHeaders,
}) => {
  return _.some(parsedConfigurations, configuration => {
    const configurationNodes = _.get(configuration, "nodes", []);
    return _.some(configurationNodes, configurationItem => {
      const configuration = _.first(_.get(configurationItem, "nodes", {}));
      const channelTypeValues = _.get(
        configuration,
        "channelTypeCategorizedValues"
      );
      return _.some(columnHeaders, ({ columnId }) => {
        return _.get(channelTypeValues, columnId) !== "NA";
      });
    });
  });
};

export {
  getColumnsForMultiColumnConfigurations,
  SUBMODULE_TYPES,
  getValidPlatforms,
  getConfigurationByPlatform,
  getSelectedCoursesForConfiguration,
  isConfigurationEnabledForEducator,
  getEditedConfiguration,
  parseConfigurationView,
  parseRequiredConfiguration,
  getCourseCountForCustomCourses,
  getTimeDropdownOptions,
  getWeekDaysDropdownOptions,
  checkIfEmailSettingsEnabled,
  getSubModuleLabel,
  getColumnConfigurationList,
  isNotificationSettingsScreen,
  getConfigurationValue,
  updateColumnConfigurations,
  getPlatformConfigurationValue,
  getUserTypeForModuleSetting,
  getCustomCoursesQueryVariablesMemoized,
  getPermissionsQueryVariablesMemoized,
  getperiodSetFilterMemoized,
  getCurriculumGlobalConstantsQueryFilterMemoized,
  getConfigInputPropsMemoized,
  getSubModuleHashMap,
  getNotificationSettingsColumnIdHashMap,
  getIsParentConfigurationDisabled,
  getCascadeModifiedConfigurations,
  getConfigurationLabelIcon,
  getSelectedCoursesForConfigurationV2,
  getSelectedCoursesForConfigurationList,
  checkIsAdminNotificationManagerEnabled,
  getAdminNotificationContext,
  getAllPlatformConfigurationValue,
  updateAdminConfigurations,
  getAdminMasterConfiguration,
  getNotificationSettingsConfig,
  checkIfWSEDisplayLogsEnabled,
  getSelectedLearningCoursesForConfigurationV2,
  getSelectedLearningCoursesForConfigurationList,
};
